import { c as _c } from "react/compiler-runtime";
import { Checkbox, FormInput, FormSelect, LocaleEnum, Popover, Tooltip, getLocaleLanguage, toast } from '@systemeio/ui';
import { AFFILIATE_PROGRAM_SETTINGS_API, createLink, deleteLink, deletePaypal, deleteWise, saveAffiliateProgram } from 'modules/profile/affiliate-program-settings/api/affiliate-program-settings-api';
import AffiliateProgramPaypal from 'modules/profile/affiliate-program-settings/components/affiliate-program-paypal';
import AffiliateProgramSettingsSkeleton from 'modules/profile/affiliate-program-settings/components/affiliate-program-settings-skeleton';
import FormAffiliateLink from 'modules/profile/affiliate-program-settings/components/form-affiliate-link';
import { AffiliateProgramSettingsLoco } from 'modules/profile/affiliate-program-settings/constants/affiliate-program-settings-loco';
import { useAffiliateProgramSettings } from 'modules/profile/affiliate-program-settings/hooks/use-affiliate-program-settings';
import { AffiliateProgramErrorsInterface, AffiliateProgramLinkCreateInterface, AffiliateProgramLinkErrorsInterface, AffiliateProgramLinksInterface, AffiliateProgramSettingsInterface } from 'modules/profile/affiliate-program-settings/types/affiliate-program-settings-interface';
import SettingsLayout from 'modules/profile/components/settings-layout';
import { settingsMenuLocoKeys } from 'modules/profile/constants/settings-menu-loco-keys';
import { NextPage } from 'next';
import React, { useEffect, useState } from 'react';
import ConfirmationModal from 'shared/components/confirmation-modal';
import ListingLayout from 'shared/components/listing-layout';
import { Table } from 'shared/components/table/table';
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { useUpdateData } from 'shared/hooks/use-update-data';
import useUserDomains from 'shared/hooks/use-user-domains';
import { usePagination } from 'shared/route-query/hooks/use-pagination';
import { LocaleDataInterface } from 'shared/types/locale-data-interface';
import { fetchWithErrors } from 'shared/utils/fetch-with-errors';
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props';
import { isValidUrl } from 'shared/utils/is-valid-url';
import { BinIcon, QuestionMarkIcon } from '@systemeio/ui/icons';
const defaultValues: AffiliateProgramSettingsInterface = {
  defaultCommission: '0',
  defaultPayoutDelay: '0',
  marketplaceBrandName: '',
  minimumPayoutAmount: '0',
  secondTierCommission: '0',
  showLeadEmailForAffiliates: false
};
const defaultErrors: AffiliateProgramErrorsInterface = {
  showLeadEmailForAffiliates: '',
  marketplaceBrandName: '',
  secondTierCommission: '',
  defaultPayoutDelay: '',
  defaultCommission: '',
  minimumPayoutAmount: ''
};
const defaultCreateLinkValues: AffiliateProgramLinkCreateInterface = {
  hostname: undefined,
  url: '',
  label: '',
  locale: undefined
};
const defaultLinkErrors: AffiliateProgramLinkErrorsInterface = {
  locale: '',
  label: '',
  url: '',
  hostname: ''
};
const AffiliateProgramSettings: NextPage = () => {
  const $ = _c(123);
  const {
    t
  } = useLocoTranslation();
  const {
    setPagination,
    pagination,
    setLimit,
    limit
  } = usePagination(true);
  let t0;
  if ($[0] !== limit || $[1] !== pagination) {
    t0 = {
      limit,
      pagination
    };
    $[0] = limit;
    $[1] = pagination;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const {
    affiliateProgram,
    mutateAffiliateProgram,
    affiliateProgramPayment,
    mutatePayment,
    affiliateProgramLinks
  } = useAffiliateProgramSettings(t0);
  let t1;
  if ($[3] !== affiliateProgram || $[4] !== affiliateProgramPayment) {
    t1 = [affiliateProgram, affiliateProgramPayment];
    $[3] = affiliateProgram;
    $[4] = affiliateProgramPayment;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== setPagination || $[7] !== t1) {
    t2 = {
      setPagination,
      api: AFFILIATE_PROGRAM_SETTINGS_API,
      customApi: `${AFFILIATE_PROGRAM_SETTINGS_API}/links`,
      constantApi: t1
    };
    $[6] = setPagination;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const updateData = useUpdateData(t2);
  const [isFetching, setIsFetching] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = Object.values(LocaleEnum).map(_temp);
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const localeData = t3;
  const {
    userDomains,
    userDomainsSelect,
    getDomainIdByName,
    getDomainNameById,
    userDefaultDomain
  } = useUserDomains(isCreateModalOpen);
  const [defaultLinkValues, setDefaultLinkValues] = useState(defaultCreateLinkValues);
  let t4;
  if ($[10] !== userDefaultDomain) {
    t4 = () => {
      if (userDefaultDomain) {
        setDefaultLinkValues(prev => ({
          ...prev,
          hostname: userDefaultDomain.name
        }));
      }
    };
    $[10] = userDefaultDomain;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== userDefaultDomain || $[13] !== userDomains) {
    t5 = [userDomains, userDefaultDomain];
    $[12] = userDefaultDomain;
    $[13] = userDomains;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setDefaultLinkValues(_temp2);
    };
    t7 = [];
    $[15] = t6;
    $[16] = t7;
  } else {
    t6 = $[15];
    t7 = $[16];
  }
  useEffect(t6, t7);
  const [tempState, setTempState] = useState(defaultValues);
  const [errors, setErrors] = useState(defaultErrors);
  const [linkErrors, setLinkErrors] = useState(defaultLinkErrors);
  const [isRemoveWiseOpen, setIsRemoveWiseOpen] = useState(false);
  const [isRemovePaypalOpen, setIsRemovePaypalOpen] = useState(false);
  let t8;
  let t9;
  if ($[17] !== affiliateProgram) {
    t8 = () => {
      if (affiliateProgram) {
        setTempState({
          ...affiliateProgram
        });
      }
    };
    t9 = [affiliateProgram];
    $[17] = affiliateProgram;
    $[18] = t8;
    $[19] = t9;
  } else {
    t8 = $[18];
    t9 = $[19];
  }
  useEffect(t8, t9);
  let t10;
  if ($[20] !== mutatePayment) {
    t10 = async () => {
      ;
      try {
        await deleteWise();
        await mutatePayment(_temp3, false);
      } catch (t11) {
        const e = t11;
        throw e;
      }
    };
    $[20] = mutatePayment;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const onWiseDelete = t10;
  let t11;
  if ($[22] !== mutatePayment) {
    t11 = async () => {
      ;
      try {
        await deletePaypal();
        await mutatePayment(_temp4, false);
      } catch (t12) {
        const e_0 = t12;
        throw e_0;
      }
    };
    $[22] = mutatePayment;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const onPaypalDelete = t11;
  let t12;
  if ($[24] !== mutatePayment) {
    t12 = async clientId => {
      await mutatePayment(data_1 => {
        if (data_1) {
          return {
            ...data_1,
            user: {
              ...data_1.user,
              systemeioUser: {
                affiliateProgramPayPalCredentials: {
                  clientId
                }
              }
            }
          };
        }
      }, false);
    };
    $[24] = mutatePayment;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const onPaypalAdd = t12;
  let t13;
  if ($[26] !== mutateAffiliateProgram || $[27] !== t || $[28] !== tempState) {
    t13 = async () => {
      await fetchWithErrors(async () => {
        setIsFetching(true);
        const response = await saveAffiliateProgram(tempState);
        setTempState(prev_1 => ({
          ...prev_1,
          ...response.data
        }));
        await mutateAffiliateProgram(response.data, false);
        setIsFetching(false);
        toast.success(t("dashboard.settings.affiliate_program.settings_modified"));
      }, setErrors);
    };
    $[26] = mutateAffiliateProgram;
    $[27] = t;
    $[28] = tempState;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const onSave = t13;
  let t14;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = [{
      key: "hostname",
      header: "",
      widthPercent: 100,
      onRender: _temp6
    }];
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const affiliateProgramLinksColumns = t14;
  let t15;
  if ($[31] !== affiliateProgramLinks || $[32] !== updateData) {
    t15 = async link_0 => {
      await deleteLink(link_0.id);
      await updateData(false, affiliateProgramLinks);
    };
    $[31] = affiliateProgramLinks;
    $[32] = updateData;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  const onLinkDelete = t15;
  let t16;
  if ($[34] !== affiliateProgramLinks || $[35] !== t || $[36] !== updateData) {
    t16 = async data_2 => {
      if (!isValidUrl(data_2.url)) {
        setLinkErrors(prev_2 => ({
          ...prev_2,
          url: t("dashboard.settings.affiliate_program.invalid_url")
        }));
        throw new Error();
      }
      await fetchWithErrors(async () => {
        await createLink(data_2);
        await updateData(true, affiliateProgramLinks);
      }, setLinkErrors);
    };
    $[34] = affiliateProgramLinks;
    $[35] = t;
    $[36] = updateData;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  const onLinkCreate = t16;
  const handlePercent = _temp7;
  const handleAmount = _temp8;
  let t17;
  if ($[38] !== t) {
    t17 = t(settingsMenuLocoKeys.affiliate_program);
    $[38] = t;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t17) {
    t18 = [{
      caption: t17,
      isStatic: true
    }];
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== affiliateProgram || $[43] !== affiliateProgramPayment || $[44] !== errors || $[45] !== isRemovePaypalOpen || $[46] !== isRemoveWiseOpen || $[47] !== onPaypalAdd || $[48] !== onPaypalDelete || $[49] !== onWiseDelete || $[50] !== t || $[51] !== tempState) {
    t19 = affiliateProgram && affiliateProgramPayment ? <form className="bg-background-secondary [&>*]:flex-1 flex h-fit w-full flex-col gap-7 overflow-x-hidden rounded-md p-5"><div className="flex flex-col gap-2">{affiliateProgramPayment.user.transferWiseAccount ? <div className="flex gap-1">{`${t("dashboard.settings.affiliate_program.wise_account")}:`}<Tooltip mode="overflow" label={affiliateProgramPayment.user.transferWiseAccount.profileName}><span className="text-green max-w-[200px] truncate">{affiliateProgramPayment.user.transferWiseAccount.profileName}</span></Tooltip><Tooltip mode="hover" label={t("dashboard.settings.affiliate_program.wise_remove_tooltip")}><button className="group outline-hidden" onClick={e_1 => {
              e_1.preventDefault();
              setIsRemoveWiseOpen(true);
            }}><BinIcon className="group-focus-visible:fill-danger hover:fill-danger main-transition-colors h-[16px] w-[16px]" /><ConfirmationModal confirmationText={t("dashboard.settings.affiliate_program.wise_remove")} opened={isRemoveWiseOpen} onClose={() => setIsRemoveWiseOpen(false)} onConfirm={onWiseDelete} /></button></Tooltip></div> : <div className="flex gap-1"><span>{t("dashboard.settings.affiliate_program.wise_not_connected")}</span><a className="primary-link" href={`https://${process.env.NEXT_PUBLIC_APP_ENV === "development" || process.env.NEXT_PUBLIC_APP_ENV === "staging" ? "sandbox.transferwise.tech" : "wise.com"}/oauth/authorize/?client_id=systemeio&redirect_uri=${window.location.origin}/dashboard/profile/transfer-wise/account/connect`} target="_blank" rel="noreferrer">{t("dashboard.settings.affiliate_program.connect")}</a></div>}{affiliateProgramPayment.user.systemeioUser.affiliateProgramPayPalCredentials ? <div className="flex gap-1">{`${t("dashboard.settings.affiliate_program.paypal_connected")}:`}<Tooltip mode="overflow" label={affiliateProgramPayment.user.systemeioUser.affiliateProgramPayPalCredentials.clientId}><span className="text-green max-w-[200px] truncate">{affiliateProgramPayment.user.systemeioUser.affiliateProgramPayPalCredentials.clientId}</span></Tooltip><Tooltip mode="hover" label={t("dashboard.settings.affiliate_program.paypal_remove_tooltip")}><button className="group outline-hidden" onClick={e_2 => {
              e_2.preventDefault();
              setIsRemovePaypalOpen(true);
            }}><BinIcon className="group-focus-visible:fill-danger hover:fill-danger main-transition-colors h-[16px] w-[16px]" /><ConfirmationModal confirmationText={t("dashboard.settings.affiliate_program.paypal_remove")} opened={isRemovePaypalOpen} onClose={() => setIsRemovePaypalOpen(false)} onConfirm={onPaypalDelete} /></button></Tooltip></div> : affiliateProgramPayment.isPaypalIntegrationAvailable ? <div className="flex gap-1"><span>{t("dashboard.settings.affiliate_program.paypal_not_connected")}</span><AffiliateProgramPaypal onPaypalAdd={onPaypalAdd} /></div> : <span>{t("dashboard.settings.affiliate_program.paypal_forbidden")}</span>}</div><div className="[&>*]:flex-1 flex w-full flex-col gap-7 xl:flex-row xl:items-end"><FormInput value={tempState.defaultCommission} type="number" max="100" min="0" onChange={e_3 => setTempState(prev_3 => ({
          ...prev_3,
          defaultCommission: handlePercent(e_3.target.value)
        }))} label={t("dashboard.settings.affiliate_program.default_commission")} error={errors.defaultCommission} /><FormInput value={tempState.secondTierCommission} type="number" onChange={e_4 => setTempState(prev_4 => ({
          ...prev_4,
          secondTierCommission: handlePercent(e_4.target.value)
        }))} label={t("dashboard.settings.affiliate_program.second_affiliate_commission")} error={errors.secondTierCommission} /></div><div className="[&>*]:flex-1 flex w-full flex-col gap-7 xl:flex-row xl:items-end"><FormInput value={tempState.minimumPayoutAmount} type="number" onChange={e_5 => setTempState(prev_5 => ({
          ...prev_5,
          minimumPayoutAmount: handleAmount(e_5.target.value)
        }))} label={t("dashboard.settings.affiliate_program.minimum_payout_amount")} error={errors.minimumPayoutAmount} /><FormInput value={tempState.defaultPayoutDelay} type="number" onChange={e_6 => setTempState(prev_6 => ({
          ...prev_6,
          defaultPayoutDelay: handlePercent(e_6.target.value)
        }))} label={t("dashboard.settings.affiliate_program.payout_delay")} error={errors.defaultPayoutDelay} /></div><FormInput value={tempState.marketplaceBrandName} onChange={e_7 => setTempState(prev_7 => ({
        ...prev_7,
        marketplaceBrandName: e_7.target.value
      }))} label={<div className="flex items-center gap-1">{t("dashboard.settings.affiliate_program.vendor_name")}{" "}<Popover label={t("dashboard.settings.affiliate_program.vendor_name_description")}>{_temp9}</Popover></div>} error={errors.marketplaceBrandName} /><Checkbox label={t("dashboard.settings.affiliate_program.allow_affilaite_view_email")} value={tempState.showLeadEmailForAffiliates} onChange={value_1 => setTempState(prev_8 => ({
        ...prev_8,
        showLeadEmailForAffiliates: value_1
      }))} /></form> : <AffiliateProgramSettingsSkeleton />;
    $[42] = affiliateProgram;
    $[43] = affiliateProgramPayment;
    $[44] = errors;
    $[45] = isRemovePaypalOpen;
    $[46] = isRemoveWiseOpen;
    $[47] = onPaypalAdd;
    $[48] = onPaypalDelete;
    $[49] = onWiseDelete;
    $[50] = t;
    $[51] = tempState;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== t) {
    t20 = t("dashboard.settings.affiliate_program.proxy_link");
    $[53] = t;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  let t21;
  let t22;
  let t23;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = async () => setIsCreateModalOpen(true);
    t22 = ["locale", "label", "url", "hostname"];
    t23 = () => setLinkErrors(defaultLinkErrors);
    $[55] = t21;
    $[56] = t22;
    $[57] = t23;
  } else {
    t21 = $[55];
    t22 = $[56];
    t23 = $[57];
  }
  let t24;
  if ($[58] !== linkErrors || $[59] !== t) {
    t24 = {
      field: "label",
      onRender: (value_2, onChange) => <FormInput value={value_2} onChange={e_8 => onChange(e_8.target.value)} label={t("global.label")} required={true} key="label-create" error={linkErrors.label} />
    };
    $[58] = linkErrors;
    $[59] = t;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  let t25;
  if ($[61] !== linkErrors || $[62] !== t) {
    t25 = {
      field: "locale",
      onRender: (value_3, onChange_0) => <FormSelect value={value_3} data={localeData} onChange={data_3 => onChange_0(data_3)} label={t("global.language")} required={true} key="locale-create" error={linkErrors.locale} />
    };
    $[61] = linkErrors;
    $[62] = t;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  let t26;
  if ($[64] !== linkErrors || $[65] !== t) {
    t26 = {
      field: "url",
      onRender: (value_4, onChange_1) => <FormInput value={value_4} onChange={e_9 => onChange_1(e_9.target.value)} label={t("global.url")} required={true} key="url-create" error={linkErrors.url} />
    };
    $[64] = linkErrors;
    $[65] = t;
    $[66] = t26;
  } else {
    t26 = $[66];
  }
  let t27;
  if ($[67] !== getDomainIdByName || $[68] !== getDomainNameById || $[69] !== linkErrors || $[70] !== t || $[71] !== userDomainsSelect) {
    t27 = {
      field: "hostname",
      onRender: (value_5, onChange_2) => <FormSelect required={true} isPreFetching={!userDomainsSelect} key="hostname-create" withoutCloseIcon={true} onChange={value_6 => value_6 && onChange_2(getDomainNameById(value_6))} value={getDomainIdByName(value_5)} label={t("global.domain")} data={userDomainsSelect} error={linkErrors.hostname} />
    };
    $[67] = getDomainIdByName;
    $[68] = getDomainNameById;
    $[69] = linkErrors;
    $[70] = t;
    $[71] = userDomainsSelect;
    $[72] = t27;
  } else {
    t27 = $[72];
  }
  let t28;
  if ($[73] !== t24 || $[74] !== t25 || $[75] !== t26 || $[76] !== t27) {
    t28 = [t24, t25, t26, t27];
    $[73] = t24;
    $[74] = t25;
    $[75] = t26;
    $[76] = t27;
    $[77] = t28;
  } else {
    t28 = $[77];
  }
  let t29;
  if ($[78] !== onLinkCreate) {
    t29 = data_4 => onLinkCreate(data_4);
    $[78] = onLinkCreate;
    $[79] = t29;
  } else {
    t29 = $[79];
  }
  let t30;
  if ($[80] !== defaultLinkValues || $[81] !== t20 || $[82] !== t28 || $[83] !== t29) {
    t30 = {
      caption: t20,
      defaultValues: defaultLinkValues,
      onOpen: t21,
      requiredFields: t22,
      onAfterLeave: t23,
      creatable: t28,
      onCreate: t29
    };
    $[80] = defaultLinkValues;
    $[81] = t20;
    $[82] = t28;
    $[83] = t29;
    $[84] = t30;
  } else {
    t30 = $[84];
  }
  let t31;
  if ($[85] !== t) {
    t31 = t("dashboard.settings.affiliate_program.proxy_links");
    $[85] = t;
    $[86] = t31;
  } else {
    t31 = $[86];
  }
  let t32;
  if ($[87] !== t) {
    t32 = t("dashboard.settings.affiliate_program.proxy_links_description-2");
    $[87] = t;
    $[88] = t32;
  } else {
    t32 = $[88];
  }
  let t33;
  if ($[89] !== t32) {
    t33 = <Popover label={t32}>{_temp10}</Popover>;
    $[89] = t32;
    $[90] = t33;
  } else {
    t33 = $[90];
  }
  let t34;
  if ($[91] !== t31 || $[92] !== t33) {
    t34 = <h1 className="flex items-center gap-1.5">{t31}{t33}</h1>;
    $[91] = t31;
    $[92] = t33;
    $[93] = t34;
  } else {
    t34 = $[93];
  }
  let t35;
  if ($[94] !== onLinkDelete) {
    t35 = {
      remove: {
        onChange: link_1 => onLinkDelete(link_1)
      }
    };
    $[94] = onLinkDelete;
    $[95] = t35;
  } else {
    t35 = $[95];
  }
  let t36;
  if ($[96] !== t) {
    t36 = t("dashboard.settings.affiliate_program.proxy_link");
    $[96] = t;
    $[97] = t36;
  } else {
    t36 = $[97];
  }
  let t37;
  if ($[98] !== limit || $[99] !== setLimit) {
    t37 = {
      limit,
      setLimit
    };
    $[98] = limit;
    $[99] = setLimit;
    $[100] = t37;
  } else {
    t37 = $[100];
  }
  let t38;
  if ($[101] !== pagination || $[102] !== setPagination) {
    t38 = {
      pagination,
      setPagination
    };
    $[101] = pagination;
    $[102] = setPagination;
    $[103] = t38;
  } else {
    t38 = $[103];
  }
  let t39;
  if ($[104] !== affiliateProgramLinks || $[105] !== t35 || $[106] !== t36 || $[107] !== t37 || $[108] !== t38) {
    t39 = <Table data={affiliateProgramLinks} columns={affiliateProgramLinksColumns} actions={t35} caption={t36} limiter={t37} paginator={t38} noHeader={true} />;
    $[104] = affiliateProgramLinks;
    $[105] = t35;
    $[106] = t36;
    $[107] = t37;
    $[108] = t38;
    $[109] = t39;
  } else {
    t39 = $[109];
  }
  let t40;
  if ($[110] !== affiliateProgramLinks || $[111] !== t30 || $[112] !== t34 || $[113] !== t39) {
    t40 = <div className="w-full"><ListingLayout counter={false} createData={t30} data={affiliateProgramLinks} title={t34} type="compact">{t39}</ListingLayout></div>;
    $[110] = affiliateProgramLinks;
    $[111] = t30;
    $[112] = t34;
    $[113] = t39;
    $[114] = t40;
  } else {
    t40 = $[114];
  }
  let t41;
  if ($[115] !== t19 || $[116] !== t40) {
    t41 = <div className="flex w-full flex-col gap-5">{t19}{t40}</div>;
    $[115] = t19;
    $[116] = t40;
    $[117] = t41;
  } else {
    t41 = $[117];
  }
  let t42;
  if ($[118] !== isFetching || $[119] !== onSave || $[120] !== t18 || $[121] !== t41) {
    t42 = <SettingsLayout titles={t18} isFetching={isFetching} onSave={onSave}>{t41}</SettingsLayout>;
    $[118] = isFetching;
    $[119] = onSave;
    $[120] = t18;
    $[121] = t41;
    $[122] = t42;
  } else {
    t42 = $[122];
  }
  return t42;
};
export const getServerSideProps = getBaseServerSideProps(AffiliateProgramSettingsLoco, 'global.settings');
export default AffiliateProgramSettings;
function _temp(locale) {
  return {
    id: locale as LocaleEnum,
    caption: getLocaleLanguage(locale)
  };
}
function _temp2(prev_0) {
  return {
    ...prev_0,
    locale: LocaleEnum.ENGLISH
  };
}
function _temp3(data) {
  if (data) {
    return {
      ...data,
      user: {
        ...data.user,
        transferWiseAccount: null
      }
    };
  }
}
function _temp4(data_0) {
  if (data_0) {
    return {
      ...data_0,
      user: {
        ...data_0.user,
        systemeioUser: {
          affiliateProgramPayPalCredentials: null
        }
      }
    };
  }
}
function _temp5(show) {
  return <QuestionMarkIcon className={`${show ? "fill-brand-primary" : "fill-fill-primary"} group-focus-visible:fill-brand-primary h-[16px] w-[16px]`} />;
}
function _temp6(link) {
  return <div className="flex w-full flex-col gap-2"><div className="flex items-center gap-1">{link.label}<Popover label={link.url}>{_temp5}</Popover></div><FormAffiliateLink value={`${process.env.NEXT_PUBLIC_SIO_HOST_SCHEME}://${link.hostname}/proxy/request/${link.hash}`} /></div>;
}
function _temp7(value) {
  return Math.max(0, Math.min(100, Number(value))).toString();
}
function _temp8(value_0) {
  return Math.max(0, Math.min(1000000, Number(value_0))).toString();
}
function _temp9(show_0) {
  return <QuestionMarkIcon className={`${show_0 ? "fill-brand-primary" : "fill-fill-primary"} group-focus-visible:fill-brand-primary h-[16px] w-[16px]`} />;
}
function _temp10(show_1) {
  return <QuestionMarkIcon className={`${show_1 ? "fill-brand-primary" : "fill-fill-primary"} group-focus-visible:fill-brand-primary`} />;
}